// 設定モーダル（各プロバイダのAPIキー / 言語モデル）の管理。localStorage に保存する。
// プロバイダタブが「主」で、APIキー入力欄とモデル一覧はタブに追従する。
import { DEFAULT_MODEL } from '../core/agent.js'
import { parseModelSelection, type Provider } from '../core/llm.js'
import { DEFAULT_MODEL_BY_PROVIDER, populateModelSelect } from './models.js'

export interface Settings {
  geminiApiKey: string
  anthropicApiKey: string
  openaiApiKey: string
  model: string
  customModelId: string
}

const SETTINGS_KEY = 'ttts-settings'

const overlay = document.getElementById('settings-overlay') as HTMLDivElement
const geminiKeyInput = document.getElementById('settings-gemini-key') as HTMLInputElement
const anthropicKeyInput = document.getElementById('settings-anthropic-key') as HTMLInputElement
const openaiKeyInput = document.getElementById('settings-openai-key') as HTMLInputElement
const providerTabs = Array.from(
  document.querySelectorAll<HTMLButtonElement>('#provider-tabs .provider-tab'),
)
const providerPanels = Array.from(
  document.querySelectorAll<HTMLDivElement>('.provider-panel'),
)
const modelSelect = document.getElementById('settings-model') as HTMLSelectElement
const customModelInput = document.getElementById('settings-custom-model') as HTMLInputElement
const customModelRow = document.getElementById('custom-model-row') as HTMLDivElement
const saveBtn = document.getElementById('settings-save') as HTMLButtonElement
const cancelBtn = document.getElementById('settings-cancel') as HTMLButtonElement
const settingsBtn = document.getElementById('settings-btn') as HTMLButtonElement
const modelBadge = document.getElementById('model-badge') as HTMLSpanElement

let settings: Settings = load()
let onSaved: ((settings: Settings) => void) | null = null

function load(): Settings {
  try {
    const stored = JSON.parse(localStorage.getItem(SETTINGS_KEY) || '{}') as Partial<Settings> & {
      apiKey?: string
    }
    return {
      // 旧形式（apiKey）はGeminiキーとして引き継ぐ
      geminiApiKey:
        typeof stored.geminiApiKey === 'string'
          ? stored.geminiApiKey
          : typeof stored.apiKey === 'string'
            ? stored.apiKey
            : '',
      anthropicApiKey: typeof stored.anthropicApiKey === 'string' ? stored.anthropicApiKey : '',
      openaiApiKey: typeof stored.openaiApiKey === 'string' ? stored.openaiApiKey : '',
      model: typeof stored.model === 'string' && stored.model ? stored.model : DEFAULT_MODEL,
      customModelId: typeof stored.customModelId === 'string' ? stored.customModelId : '',
    }
  } catch {
    return {
      geminiApiKey: '',
      anthropicApiKey: '',
      openaiApiKey: '',
      model: DEFAULT_MODEL,
      customModelId: '',
    }
  }
}

export function getSettings(): Settings {
  return settings
}

/** 選択中プロバイダに対応するAPIキーを返す */
export function apiKeyFor(provider: Provider): string {
  switch (provider) {
    case 'anthropic':
      return settings.anthropicApiKey
    case 'openai':
      return settings.openaiApiKey
    default:
      return settings.geminiApiKey
  }
}

export function hasAnyApiKey(): boolean {
  return Boolean(settings.geminiApiKey || settings.anthropicApiKey || settings.openaiApiKey)
}

/** 実際にAPIへ渡すモデル指定値（ユーザー指定モデル対応） */
export function effectiveModel(): string {
  return settings.model === 'custom' ? settings.customModelId : settings.model
}

const keyInputs: Record<Provider, HTMLInputElement> = {
  gemini: geminiKeyInput,
  anthropic: anthropicKeyInput,
  openai: openaiKeyInput,
}

function hasModelOption(value: string): boolean {
  return Boolean(modelSelect.querySelector(`option[value="${CSS.escape(value)}"]`))
}

/**
 * モデル一覧を指定プロバイダのものに絞り込む。
 * 選択中の値が新しい一覧にも存在すれば維持し、なければプロバイダの既定モデルにする
 * （「ユーザー指定」は全プロバイダの一覧に含まれるため維持される）。
 */
function repopulateModelSelect(provider: Provider): void {
  const previous = modelSelect.value
  populateModelSelect(modelSelect, provider)
  modelSelect.value = hasModelOption(previous) ? previous : DEFAULT_MODEL_BY_PROVIDER[provider]
  customModelRow.hidden = modelSelect.value !== 'custom'
}

/** 選択中のプロバイダタブを切り替え、キー入力欄とモデル一覧を追従させる */
function setActiveProviderTab(provider: Provider, options: { focus?: boolean } = {}): void {
  for (const tab of providerTabs) {
    tab.classList.toggle('active', tab.dataset['provider'] === provider)
  }
  for (const panel of providerPanels) {
    panel.hidden = panel.dataset['provider'] !== provider
  }
  repopulateModelSelect(provider)
  if (options.focus) keyInputs[provider].focus()
}

/** キー設定済みのプロバイダタブに目印を付ける（入力中の値を反映） */
function updateKeyMarkers(): void {
  for (const tab of providerTabs) {
    const provider = tab.dataset['provider'] as Provider
    tab.classList.toggle('has-key', keyInputs[provider].value.trim() !== '')
  }
}

/** モデル選択値からプロバイダを求める（不正な値はGemini扱い） */
function providerOfModelValue(value: string): Provider {
  try {
    return parseModelSelection(value).provider
  } catch {
    return 'gemini'
  }
}

/** 保存されている設定のプロバイダ（カスタムはID から判定） */
function providerOfSettings(): Provider {
  return providerOfModelValue(
    settings.model === 'custom' ? settings.customModelId : settings.model,
  )
}

function updateModelBadge(): void {
  const option = modelSelect.querySelector<HTMLOptionElement>(
    `option[value="${CSS.escape(settings.model)}"]`,
  )
  modelBadge.textContent =
    settings.model === 'custom'
      ? settings.customModelId || 'モデル未設定'
      : option
        ? (option.textContent ?? settings.model)
        : settings.model
}

export function openSettings(): void {
  geminiKeyInput.value = settings.geminiApiKey
  anthropicKeyInput.value = settings.anthropicApiKey
  openaiKeyInput.value = settings.openaiApiKey
  customModelInput.value = settings.customModelId
  // 選択中モデルのプロバイダのタブを開く（未設定キーの案内から来たときに迷わない）
  const provider = providerOfSettings()
  setActiveProviderTab(provider)
  modelSelect.value = hasModelOption(settings.model)
    ? settings.model
    : DEFAULT_MODEL_BY_PROVIDER[provider]
  customModelRow.hidden = modelSelect.value !== 'custom'
  updateKeyMarkers()
  overlay.hidden = false
  keyInputs[provider].focus()
}

export function closeSettings(): void {
  overlay.hidden = true
}

export function initSettings(handlers: { onSaved: (settings: Settings) => void }): void {
  onSaved = handlers.onSaved

  // モデルバッジ表示用に、保存中モデルのプロバイダで一覧を初期化しておく
  const provider = providerOfSettings()
  setActiveProviderTab(provider)
  if (hasModelOption(settings.model)) modelSelect.value = settings.model

  settingsBtn.addEventListener('click', openSettings)
  cancelBtn.addEventListener('click', closeSettings)
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) closeSettings()
  })
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !overlay.hidden) closeSettings()
  })

  for (const tab of providerTabs) {
    tab.addEventListener('click', () => {
      setActiveProviderTab(tab.dataset['provider'] as Provider, { focus: true })
    })
  }
  for (const input of Object.values(keyInputs)) {
    input.addEventListener('input', updateKeyMarkers)
  }

  modelSelect.addEventListener('change', () => {
    customModelRow.hidden = modelSelect.value !== 'custom'
  })
  // ユーザー指定モデルのIDが別プロバイダを指していたらタブを追従させる
  customModelInput.addEventListener('input', () => {
    const value = customModelInput.value.trim()
    if (!value) return
    const provider = providerOfModelValue(value)
    const activeTab = providerTabs.find((t) => t.classList.contains('active'))
    if (activeTab?.dataset['provider'] !== provider) setActiveProviderTab(provider)
  })

  saveBtn.addEventListener('click', () => {
    const model = modelSelect.value
    const customModelId = customModelInput.value.trim()

    if (model === 'custom' && !customModelId) {
      customModelInput.focus()
      return
    }

    settings = {
      geminiApiKey: geminiKeyInput.value.trim(),
      anthropicApiKey: anthropicKeyInput.value.trim(),
      openaiApiKey: openaiKeyInput.value.trim(),
      model,
      customModelId,
    }
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings))
    updateModelBadge()
    closeSettings()
    onSaved?.(settings)
  })

  updateModelBadge()
}
